import { useEffect, useState } from 'react';
import { useT } from '../i18n';
import { selectedLotStore, type LotSpec } from '../data/plots';

/**
 * Floating bottom-right share button. Used by both viewer modes — Cesium
 * and Leaflet — so the position logic (pushing up when LotDetails is
 * visible) lives in one place. Copies the current `window.location.href`
 * (which already includes `?lot=…` when a lot is selected) via
 * `navigator.share` when available, falling back to the clipboard and
 * finally `window.prompt`.
 */
export function ShareButton() {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const [selectedLot, setSelectedLot] = useState<LotSpec | null>(
    selectedLotStore.get(),
  );
  const [bottom, setBottom] = useState(70);

  useEffect(() => {
    const unsub = selectedLotStore.subscribe(setSelectedLot);
    return () => {
      unsub();
    };
  }, []);

  // Hover 6 px above whatever's at the bottom of the screen — the lot
  // details panel when a lot is selected, otherwise the footer (Reset /
  // Locate me / Contact / 3D-toggle pill). Same gap in both cases.
  useEffect(() => {
    const targetId = selectedLot ? 'lot-details-panel' : 'manglillo-footer';
    const recalc = () => {
      const el = document.getElementById(targetId);
      if (!el) {
        setBottom(70);
        return;
      }
      const GAP = 12;
      const wanted = window.innerHeight - el.getBoundingClientRect().top + GAP;
      // Don't ride above the top-right widgets (the help `?` at top:52 and
      // the language flag at top:92, each 32 px tall). Cap the share button
      // so its top edge stays below those.
      const TOP_WIDGETS_BOTTOM = 92 + 32 + 8;
      const SHARE_HEIGHT = 40;
      const maxBottom = window.innerHeight - SHARE_HEIGHT - TOP_WIDGETS_BOTTOM;
      setBottom(Math.min(wanted, maxBottom));
    };
    const raf = requestAnimationFrame(recalc);
    const el = document.getElementById(targetId);
    const ro = el ? new ResizeObserver(recalc) : null;
    if (el && ro) ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [selectedLot]);

  const handleShare = async () => {
    // Floating share intentionally carries only the live URL (?lot=<id>) —
    // no camera/zoom — so the share message stays short. Use the "Share
    // this view" entry in the menu for a snapshot of the current view.
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Se Venden Lotes: El Manglillo.',
          text: 'Se Venden Lotes: El Manglillo.',
          url,
        });
        return;
      }
    } catch {
      // user cancelled or share failed — fall through to clipboard
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt('Copia el enlace:', url);
    }
  };

  // Pin the button to a fixed base offset and drive the actual position via
  // `transform: translateY(...)`. Transitioning `transform` is composited
  // (no layout per frame), unlike transitioning `bottom`, which PSI flagged
  // as "non-composited animation".
  const BASE_BOTTOM = 70;
  const offsetY = BASE_BOTTOM - bottom;
  return (
    <button
      type="button"
      onClick={handleShare}
      aria-label={t('share_view')}
      title={copied ? t('share_copied') : t('share_view')}
      style={{
        position: 'absolute',
        bottom: BASE_BOTTOM,
        right: 16,
        width: 40,
        height: 40,
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.25)',
        background: 'rgba(0,0,0,0.62)',
        color: 'white',
        cursor: 'pointer',
        fontSize: 14,
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        padding: 0,
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        transform: `translateY(${offsetY}px)`,
        transition: 'transform 0.2s ease',
        willChange: 'transform',
        zIndex: 6,
      }}
    >
      {copied ? '✓' : (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
      )}
    </button>
  );
}
